import {UI} from '../libs/palette.js';
import {rotateTitle} from '../libs/orientation.js';

/**
 * RotatePrompt: the full-screen "Turn sideways to get WINSTON running" card
 * shown over the game on a phone or tablet held upright (src/libs/orientation.js decides when),
 * and the landscape helpers the start screen's START uses on a touch device.
 */

const {INK, LIME, PAPER, SLATE} = UI;

// A 16x16 pixel phone, drawn upright and turned on its side by the animation.
// The start screen's sideways nudge uses it too.
export const PHONE = `<svg class="rp-phone" viewBox="0 0 16 16" aria-hidden="true" shape-rendering="crispEdges">
  <path fill="${LIME}" d="M4 1h8v1H4zM3 2h1v12H3zM12 2h1v12h-1zM4 14h8v1H4z"/>
  <path fill="${SLATE}" d="M5 3h6v9H5z"/>
  <path fill="${LIME}" d="M7 12h2v1H7z"/>
</svg>`;

// a quarter-turn arrow over it, curving the way the phone turns
const ARROW = `<svg class="rp-arrow" viewBox="0 0 11 6" aria-hidden="true" shape-rendering="crispEdges">
  <path fill="${PAPER}" d="M3 0h5v1H3zM2 1h1v1H2zM8 1h1v1H8zM1 2h1v2H1zM9 2h1v2H9zM0 4h3v1H0zM1 5h1v1H1z"/>
</svg>`;

const STYLES = `
#rotate-prompt {
  position: fixed; inset: 0; z-index: 40; background: ${INK}; color: ${PAPER};
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 28px;
  padding: max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right))
    max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left));
  font-family: 'Press Start 2P', monospace; text-align: center;
  touch-action: none;
}
#rotate-prompt[hidden] { display: none; }
#rotate-prompt .rp-icon { position: relative; width: 128px; height: 128px; margin-top: 40px; }
#rotate-prompt .rp-phone { width: 128px; height: 128px; image-rendering: pixelated; display: block; }
#rotate-prompt .rp-arrow { position: absolute; left: 20px; top: -40px; width: 88px; height: 48px; image-rendering: pixelated; }
#rotate-prompt h2 {
  margin: 0; font-size: 16px; line-height: 1.7; font-weight: 400; color: ${LIME};
  max-width: 320px; text-shadow: 3px 3px 0 ${SLATE};
}
#rotate-prompt p { margin: 0; font-size: 10px; line-height: 1.6; color: ${PAPER}; opacity: .8; }
@media (prefers-reduced-motion: no-preference) {
  /* upright, a quarter turn to the left, a beat on its side, back */
  #rotate-prompt .rp-phone { animation: rp-turn 2.4s ease-in-out infinite; }
}
@keyframes rp-turn {
  0%, 20% { transform: rotate(0deg); }
  45%, 80% { transform: rotate(-90deg); }
  100% { transform: rotate(0deg); }
}
`;

let root = null;

/** Builds the card once, hidden. */
function mount() {
  if (root) {
    return root;
  }
  const style = document.createElement('style');
  style.textContent = STYLES;
  document.head.appendChild(style);
  root = document.createElement('div');
  root.id = 'rotate-prompt';
  root.hidden = true;
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'rotate-prompt-title');
  root.innerHTML = `<div class="rp-icon">${PHONE}${ARROW}</div>
    <h2 id="rotate-prompt-title">${rotateTitle()}</h2>
    <p>Your game is paused</p>`;
  document.body.appendChild(root);
  return root;
}

/**
 * Show or hide the card.
 * @param {boolean} shown
 * @param {string} [dogName] the picked dog, named on the card
 */
export function setRotatePrompt(shown, dogName) {
  if (!shown && !root) {
    return;
  }
  const card = mount();
  if (shown) {
    card.querySelector('#rotate-prompt-title').textContent = rotateTitle(dogName);
  }
  card.hidden = !shown;
}

/** @returns {boolean} whether the card is up */
export function rotatePromptShown() {
  return !!root && !root.hidden;
}

/**
 * Whether the primary pointer is a finger: a phone or tablet, not a desktop
 * (even one with a narrow window).
 * @returns {boolean}
 */
export function isTouchDevice() {
  try {
    return !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  } catch (e) {
    return false;
  }
}

// the fullscreen request from START, which the landscape lock has to wait for
let fullscreenPending = Promise.resolve();

/**
 * On a touch device, asks for fullscreen, which hides the browser bar and is
 * what Chrome on Android needs before it will lock the orientation. Call it
 * straight from the START tap: browsers only allow it from a user gesture.
 * iOS Safari has no page fullscreen, and a refusal is ignored either way.
 */
export function requestGameFullscreen() {
  if (!isTouchDevice()) {
    return;
  }
  try {
    const el = document.documentElement;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (!request || document.fullscreenElement || document.webkitFullscreenElement) {
      return;
    }
    fullscreenPending = Promise.resolve(request.call(el, {navigationUI: 'hide'})).catch(() => null);
  } catch (e) {
    fullscreenPending = Promise.resolve();
  }
}

/**
 * Locks the screen to landscape once the fullscreen request has settled.
 * Chrome on Android allows it in fullscreen; iOS has no lock and a desktop
 * refuses it, which is fine: the rotate prompt covers those.
 * @returns {Promise} never rejects
 */
export function lockLandscape() {
  if (!isTouchDevice()) {
    return Promise.resolve();
  }
  return fullscreenPending.then(() => {
    const orientation = window.screen && window.screen.orientation;
    if (orientation && typeof orientation.lock === 'function') {
      return orientation.lock('landscape');
    }
    return null;
  }).catch(() => null);
}
